import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const schedulingComponent: ComponentDef = {
  id: 'scheduling',
  name: 'Scheduling',
  description: 'Schedule training or inference jobs across GPUs to maximize utilization and fairness.',
  category: 'optimization',
  params: [
    { name: 'n_gpus', label: 'GPUs', type: 'number', default: 8, min: 1, max: 1024, step: 1 },
    { name: 'jobs', label: 'Jobs', type: 'number', default: 16, min: 1, max: 256, step: 1 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: 'request',
      options: [
        { label: 'Request Scheduling', value: 'request' },
        { label: 'PD Disaggregated', value: 'pd_disagg' },
        { label: 'Cluster Job', value: 'cluster' },
        { label: 'Pipeline Schedule', value: 'pipeline' },
        { label: 'Data Loading', value: 'data_loading' },
        { label: 'KV Eviction', value: 'kv_eviction' },
        { label: 'CPU-GPU Offload', value: 'cpu_gpu_offload' },
        { label: 'Compute-Comm Overlap', value: 'compute_comm' },
        { label: 'Chunked Prefill (Sarathi)', value: 'chunked_prefill' }
      ]
    }
  ],
  buildGraph: (params) => {
    const n_gpus = Number(params.n_gpus);
    const jobs = Number(params.jobs);
    const variant = String(params.variant);

    const g = new GraphBuilder();

    if (variant === 'pd_disagg') {
      // Prefill-Decode Disaggregated scheduling
      g.input('requests', [jobs]);
      g.router('prefill_queue', 'requests', [jobs]);
      g.scale('prefill_gpus', 'prefill_queue', `Prefill ${Math.ceil(n_gpus / 2)} GPUs`);
      g.router('decode_queue', 'requests', [jobs]);
      g.scale('decode_gpus', 'decode_queue', `Decode ${Math.floor(n_gpus / 2)} GPUs`);
      g.concat('scheduled', ['prefill_gpus', 'decode_gpus'], [jobs]);
      g.metric('util', 'scheduled', 'Utilization');
      g.metric('latency', 'util', 'TTFT + TPOT');
    } else if (variant === 'cluster') {
      // Cluster job scheduling with gang scheduling
      g.input('job_queue', [jobs]);
      g.scale('priority', 'job_queue', 'Priority Sort');
      g.router('gang_scheduler', 'priority', [jobs]);
      g.scale('node_alloc', 'gang_scheduler', `${n_gpus} GPUs`);
      g.allreduce('sync_barrier', 'node_alloc', [jobs]);
      g.metric('util', 'sync_barrier', 'Cluster Util');
      g.metric('wait_time', 'util', 'Queue Wait');
    } else if (variant === 'pipeline') {
      // Pipeline schedule (1F1B / interleaved)
      g.input('micro_batches', [jobs]);
      g.split('stages', 'micro_batches', [jobs], `${n_gpus} stages`);
      g.scale('fwd_pass', 'stages', 'Forward');
      g.scale('bwd_pass', 'stages', 'Backward');
      g.concat('pipeline', ['fwd_pass', 'bwd_pass'], [jobs]);
      g.scale('1f1b', 'pipeline', '1F1B Schedule');
      g.metric('bubble', '1f1b', 'Bubble Ratio');
      g.metric('throughput', 'bubble', 'Throughput');
    } else if (variant === 'data_loading') {
      // Data loading pipeline
      g.input('dataset', [jobs]);
      g.scale('shuffle', 'dataset', 'Shuffle');
      g.scale('tokenize', 'shuffle', 'Tokenize');
      g.cache('prefetch', 'tokenize', [jobs]);
      g.scale('batch', 'prefetch', 'Batch');
      g.scale('pin_mem', 'batch', 'Pin to GPU');
      g.metric('throughput', 'pin_mem', 'Samples/s');
      g.metric('stall', 'throughput', 'GPU Stall %');
    } else if (variant === 'kv_eviction') {
      // KV cache eviction scheduling
      g.input('requests', [jobs]);
      g.cache('kv_store', 'requests', [jobs, n_gpus]);
      g.scale('lru_policy', 'kv_store', 'LRU Policy');
      g.mask('evict_mask', 'lru_policy', [jobs, n_gpus]);
      g.scale('freed', 'evict_mask', 'Freed Slots');
      g.router('admit', 'requests', [jobs]);
      g.metric('hit_rate', 'admit', 'Cache Hit Rate');
      g.metric('memory', 'hit_rate', 'Memory Usage');
    } else if (variant === 'cpu_gpu_offload') {
      // CPU-GPU offload scheduling
      g.input('layers', [jobs]);
      g.split('placement', 'layers', [jobs], 'GPU / CPU');
      g.scale('gpu_layers', 'placement', `GPU (${n_gpus} layers)`);
      g.cache('cpu_layers', 'placement', [jobs]);
      g.scale('cpu_compute', 'cpu_layers', 'CPU Compute');
      g.p2p('transfer', 'cpu_compute', [jobs]);
      g.concat('merged', ['gpu_layers', 'transfer'], [jobs]);
      g.metric('speedup', 'merged', 'Speedup');
      g.metric('pci_bw', 'speedup', 'PCIe BW');
    } else if (variant === 'compute_comm') {
      // Compute-communication overlap
      g.input('chunks', [jobs]);
      g.split('overlap', 'chunks', [jobs], 'Compute / Comm');
      g.scale('compute', 'overlap', 'Compute Chunk');
      g.allreduce('comm', 'overlap', [jobs]);
      g.add('overlap_result', 'compute', 'comm');
      g.metric('overlap_pct', 'overlap_result', 'Overlap %');
      g.metric('efficiency', 'overlap_pct', 'Efficiency');
    } else if (variant === 'chunked_prefill') {
      // Chunked prefill (Sarathi) - split prefills into chunks to interleave with decodes
      g.input('requests', [jobs]);
      g.split('prefill_decode', 'requests', [jobs], 'Prefill / Decode');
      g.scale('chunk_prefill', 'prefill_decode', `Chunk size ${Math.min(jobs, 4)}`);
      g.scale('decode_tokens', 'prefill_decode', 'Decode Tokens');
      g.concat('batch', ['chunk_prefill', 'decode_tokens'], [jobs]);
      g.scale('gpu_exec', 'batch', `${n_gpus} GPUs`);
      g.metric('ttft', 'gpu_exec', 'TTFT');
      g.metric('tpot', 'ttft', 'TPOT');
    } else {
      // Request scheduling (default)
      g.input('queue', [jobs]);
      g.scale('priority', 'queue', 'Priority');
      g.router('scheduler', 'priority', [jobs]);
      g.scale('gpu_pool', 'scheduler', `${n_gpus} GPUs`);
      g.scale('packing', 'gpu_pool', 'Packing');
      g.metric('util', 'packing', 'Utilization');
      g.metric('latency', 'util', 'Latency');
    }

    g.output('out', variant === 'chunked_prefill' ? 'tpot' :
             variant === 'compute_comm' ? 'efficiency' :
             variant === 'cpu_gpu_offload' ? 'pci_bw' :
             variant === 'kv_eviction' ? 'memory' :
             variant === 'data_loading' ? 'stall' :
             variant === 'pipeline' ? 'throughput' :
             variant === 'cluster' ? 'wait_time' :
             variant === 'pd_disagg' ? 'latency' :
             'latency');
    return g.build();
  }
};
